import { z } from 'zod'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'
import { optionalProjectId } from '../../common/optionalFields'

export const ConnectorType = {
  GoogleDrive: 'google-drive',
  SharePoint: 'sharepoint',
  WebCrawler: 'web-crawler'
} as const

export type ConnectorType = (typeof ConnectorType)[keyof typeof ConnectorType]

export const ConnectorState = {
  Incomplete: 'incomplete',
  Authorised: 'authorised',
  Configured: 'configured',
  Syncing: 'syncing',
  Deleting: 'deleting',
  Error: 'error',
  Active: 'active',
  Cancelled: 'cancelled'
} as const

export type ConnectorState = (typeof ConnectorState)[keyof typeof ConnectorState]

export const ConnectorStatusDetail = {
  None: '',
  AuthenticationError: 'error_authentication',
  WebCrawlerPageLimit: 'warning_webcrawler_pagelimit',
  SyncFailed: 'error_sync_failed',
  SyncTimedOut: 'error_sync_timed_out',
  FileProcessing: 'error_file_processing',
  UserCancelled: 'cancelled_user_request'
} as const

export type ConnectorStatusDetail = (typeof ConnectorStatusDetail)[keyof typeof ConnectorStatusDetail]

export const FileSyncState = {
  Unknown: 'unknown',
  Excluded: 'excluded',
  Pending: 'pending',
  Completed: 'completed',
  Error: 'error'
} as const

export type FileSyncState = (typeof FileSyncState)[keyof typeof FileSyncState]

export const ProcessingDetails = {
  None: 'none',
  PdfNoText: 'pdf_no_text',
  EmptyFolder: 'empty_folder',
  FileCorrupt: 'file_corrupt',
  FileTooLarge: 'file_too_large',
  UnsupportedType: 'unsupported_type',
  SourceMissing: 'source_missing',
  ExtractionTimeout: 'extraction_timeout',
  TreeCycle: 'tree_cycle'
} as const

export type ProcessingDetails = (typeof ProcessingDetails)[keyof typeof ProcessingDetails]

export function connectorLabel(type: string): string {
  switch (type) {
    case ConnectorType.GoogleDrive:
      return 'Google Drive'
    case ConnectorType.SharePoint:
      return 'SharePoint'
    case ConnectorType.WebCrawler:
      return 'Website Crawler'
    default:
      return type
  }
}

type ConnectorStateLabel = {
  label: string
  message: string | undefined
  colorClass: string
}

export function connectorStateLabel(state: string, statusDetails: string): ConnectorStateLabel {
  switch (state) {
    case ConnectorState.Incomplete:
      return { label: 'Incomplete', message: 'Authorisation is pending', colorClass: 'text-muted-foreground' }
    case ConnectorState.Authorised:
      return { label: 'Authorised', message: 'Configuration is pending', colorClass: 'text-yellow-500' }
    case ConnectorState.Configured:
      return { label: 'Configured', message: undefined, colorClass: 'text-green-500' }
    case ConnectorState.Syncing:
      return { label: 'Syncing', message: undefined, colorClass: 'text-blue-500' }
    case ConnectorState.Deleting:
      return { label: 'Deleting', message: undefined, colorClass: 'text-orange-400' }
    case ConnectorState.Cancelled:
      return {
        label: 'Cancelled',
        message: 'The sync was cancelled. Resync to start again.',
        colorClass: 'text-orange-400'
      }
    case ConnectorState.Active:
      if (statusDetails === ConnectorStatusDetail.WebCrawlerPageLimit) {
        return {
          label: 'Active',
          message: 'Page limit reached. Some pages may not have been crawled.',
          colorClass: 'text-orange-400'
        }
      }
      return { label: 'Active', message: undefined, colorClass: 'text-green-500' }
    case ConnectorState.Error:
      switch (statusDetails) {
        case ConnectorStatusDetail.AuthenticationError:
          return { label: 'Authentication Error', message: 'Re-authentication is required', colorClass: 'text-red-500' }
        case ConnectorStatusDetail.SyncTimedOut:
          return {
            label: 'Sync Timed Out',
            message: 'The sync exceeded the time limit. Try again or reduce the content scope.',
            colorClass: 'text-red-500'
          }
        case ConnectorStatusDetail.SyncFailed:
          return {
            label: 'Sync Failed',
            message: 'The sync could not complete. Try resyncing or contact support.',
            colorClass: 'text-red-500'
          }
        case ConnectorStatusDetail.FileProcessing:
          return {
            label: 'File Processing Errors',
            message: 'Some files could not be processed. Check the file list for details.',
            colorClass: 'text-red-500'
          }
        default:
          return {
            label: 'Error',
            message: 'The connector encountered a problem. Try resyncing.',
            colorClass: 'text-red-500'
          }
      }
    default:
      return { label: state, message: undefined, colorClass: 'text-muted-foreground' }
  }
}

// Mirrors backend services.ContentConnector
export const ConnectorSchema = z
  .object({
    id: z.number(),
    orgId: z.number(),
    projectId: z.number().nullable(),
    connectorType: z.enum(ConnectorType),
    connectorDescription: z.string(),
    state: z.enum(ConnectorState),
    statusDetails: z.enum(ConnectorStatusDetail),
    lastSyncedAtTsMs: z.number().nullable(),
    createdById: z.number(),
    createdByFirstName: z.string(),
    createdBySurname: z.string().nullable(),
    createdOnTsMs: z.number(),
    updatedById: z.number(),
    updatedByFirstName: z.string(),
    updatedBySurname: z.string().nullable(),
    updatedAtTsMs: z.number()
  })
  .describe('Connector')

export type Connector = z.infer<typeof ConnectorSchema>

export const ListConnectorsResponseSchema = z
  .object({
    connectors: z.array(ConnectorSchema)
  })
  .describe('ListConnectorsResponse')

export type ListConnectorsResponse = z.infer<typeof ListConnectorsResponseSchema>

// Mirrors backend services.TreeItem
export const TreeItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    isFolder: z.boolean(),
    children: z.array(z.string()),
    checked: z.string(),
    syncState: z.enum(FileSyncState),
    processingDetails: z.enum(ProcessingDetails).optional(),
    lastProcessedAtTsMs: z.number().optional()
  })
  .describe('TreeItem')

export type TreeItem = z.infer<typeof TreeItemSchema>

export const WebCrawlerItemSchema = z
  .object({
    url: z.string(),
    state: z.string().optional()
  })
  .describe('WebCrawlerItem')

export type WebCrawlerItem = z.infer<typeof WebCrawlerItemSchema>

const AddConnectorResponseSchema = z
  .object({
    connectorId: z.number(),
    redirectUrl: z.string()
  })
  .describe('AddConnectorResponse')

export type AddConnectorResponse = z.infer<typeof AddConnectorResponseSchema>

const DeleteConnectorResponseSchema = z
  .object({
    success: z.boolean()
  })
  .describe('DeleteConnectorResponse')

const GetCurrentConfigResponseSchema = z
  .object({
    fileConfig: z.array(TreeItemSchema).nullable(),
    webCrawlerConfig: WebCrawlerItemSchema.nullable()
  })
  .describe('GetCurrentConfigResponse')

const GetItemResponseSchema = z
  .object({
    item: TreeItemSchema
  })
  .describe('GetItemResponse')

const GetChildrenWithDataResponseSchema = z
  .object({
    childrenWithData: z.array(
      z.object({
        id: z.string(),
        data: TreeItemSchema
      })
    )
  })
  .describe('GetChildrenWithDataResponse')

export type ChildrenWithData = z.infer<typeof GetChildrenWithDataResponseSchema>['childrenWithData']

export async function listConnectors(projectId?: number): Promise<ListConnectorsResponse> {
  const res = await fetch('/api/content/connectors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
  const data = await res.json()
  return parseDataOfType(data, ListConnectorsResponseSchema)
}

export async function addConnector(
  connectorType: string,
  connectorDescription: string,
  projectId?: number
): Promise<AddConnectorResponse> {
  const res = await fetch('/api/content/connectors/add', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorType, ...optionalProjectId(projectId), connectorDescription })
  })
  await apiErrorHandler(res)
  const data = await res.json()
  return parseDataOfType(data, AddConnectorResponseSchema)
}

export async function deleteConnector(connectorId: number, projectId?: number): Promise<boolean> {
  const res = await fetch('/api/content/connectors/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorId, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
  const data = await res.json()
  const parsed = parseDataOfType(data, DeleteConnectorResponseSchema)
  return parsed.success
}

export async function saveConnectorDescription(connectorId: number, connectorDescription: string): Promise<void> {
  const res = await fetch('/api/content/connectors/edit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      connectorId,
      connectorDescription
    })
  })
  await apiErrorHandler(res)
}

export async function getConnectorCurrentConfig(
  connectorId: number,
  projectId?: number
): Promise<{ fileConfig: Array<TreeItem> | null; webCrawlerConfig: WebCrawlerItem | null }> {
  const res = await fetch('/api/content/connectors/getCurrentConfig', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorId, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
  const data = await res.json()
  return parseDataOfType(data, GetCurrentConfigResponseSchema)
}

export async function saveConnectorConfig(
  connectorId: number,
  projectId?: number,
  fileConfig?: Array<TreeItem>,
  webCrawlerConfig?: WebCrawlerItem
): Promise<void> {
  const res = await fetch('/api/content/connectors/saveConfig', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      connectorId,
      ...optionalProjectId(projectId),
      fileConfig: fileConfig ?? [],
      webCrawlerConfig: webCrawlerConfig ?? { url: '' }
    })
  })
  await apiErrorHandler(res)
}

const ReauthConnectorResponseSchema = z
  .object({
    redirectUrl: z.string()
  })
  .describe('ReauthConnectorResponse')

export async function reauthConnector(connectorId: number, projectId?: number): Promise<string> {
  const res = await fetch('/api/content/connectors/reauth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorId, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
  const data = await res.json()
  const parsed = parseDataOfType(data, ReauthConnectorResponseSchema)
  return parsed.redirectUrl
}

export async function resyncConnector(connectorId: number, projectId?: number): Promise<void> {
  const res = await fetch('/api/content/connectors/resync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorId, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
}

export async function cancelSyncConnector(connectorId: number, projectId?: number): Promise<void> {
  const res = await fetch('/api/content/connectors/cancel-sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorId, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
}

const GetWebCrawlerSubUrlsResponseSchema = z
  .object({
    subUrls: z.array(z.string())
  })
  .describe('GetWebCrawlerSubUrlsResponse')

export async function getWebCrawlerSubUrls(connectorId: number, projectId?: number): Promise<Array<string>> {
  const res = await fetch('/api/content/connectors/getWebCrawlerSubUrls', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorId, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
  const data = await res.json()
  const parsed = parseDataOfType(data, GetWebCrawlerSubUrlsResponseSchema)
  return parsed.subUrls
}

export async function connectorGetItem(connectorId: number, itemId: string, projectId?: number): Promise<TreeItem> {
  const res = await fetch('/api/content/connectors/getItem', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorId, itemId, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
  const data = await res.json()
  const parsed = parseDataOfType(data, GetItemResponseSchema)
  return parsed.item
}

export async function connectorGetChildrenWithData(
  connectorId: number,
  itemId: string,
  projectId?: number
): Promise<ChildrenWithData> {
  const res = await fetch('/api/content/connectors/getChildrenWithData', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ connectorId, itemId, ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
  const data = await res.json()
  const parsed = parseDataOfType(data, GetChildrenWithDataResponseSchema)
  return parsed.childrenWithData
}
